import { v4 } from "uuid"
import { fetcher } from "../../../utils/api"
import { IFile, IFolder, IFolderContent } from "../types"
import { confirmFileUpload } from "@/modules/storage/utils/confirmFileUpload"
import { StatusCode } from "@/core/generated/api/enums/status_code"
import {
  COOKIE_AUTH_ENABLED,
  getCsrfHeaders,
} from "@/core/generated/api/auth_cookies"
import { getToken } from "@/utils/api"
import { withTabSpaceHeader } from "@/utils/tabSpace"

const API_URL = process.env.NEXT_PUBLIC_API_URL

export const getAllFolders = async (): Promise<IFolder[]> => {
  return await fetcher(`/storage/folders`)
}

export const getSubfolders = async (
  uid?: string,
): Promise<Omit<IFolderContent, "files">> => {
  return await fetcher(`/storage/folders/${uid ? uid : "root"}/subfolders`)
}

export const getFolderContent = async (
  uid?: string,
): Promise<IFolderContent> => {
  return await fetcher(`/storage/folders/${uid ? uid : "root"}`)
}

export const getFileByUid = async (
  uid: string,
): Promise<IFile & { buffer: string }> => {
  return await fetcher(`/storage/files/${uid}`)
}

export const getSharedFiles = async (space_uid: string) => {
  return await fetcher(`/storage/files/shared?space=${space_uid}`)
}

export const createFolder = async (
  name: string,
  uid?: string,
): Promise<{ folder: IFolder; didexist: boolean }> => {
  return await fetcher("/storage/folders", {
    method: "POST",
    body: JSON.stringify({ name, parent: uid }),
  })
}

// This method sometimes return null even with a 200 status code, but it's unclear why.
export const zipFiles = async (
  folders: string[],
  files: string[],
): Promise<{ base64: string } | null> => {
  return await fetcher("/storage/zip", {
    method: "POST",
    body: JSON.stringify({ folders, files }),
  })
}

export enum FileUploadError {
  NOT_DEFINED,
  OTHER,
  /** {@link MAX_FILE_SIZE} */
  FILE_SIZE_LIMIT_REACHED,
}

/**
 * `28 Mebibytes` = `29360128 bytes`
 *
 * The node.js instance on GCP has a response size limit of 32 MiB.
 *
 * For this reason, the file upload size limit is restricted here, because users would not be able to download the files anyway.
 *
 * This value is set to 28 MiB for good measure.
 */
const MAX_FILE_SIZE = 2 ** 20 * 28

export const uploadFiles = async (
  uid: string,
  files: FormData,
): Promise<{
  files: IFile[]
  success: boolean
  errorType: FileUploadError
}> => {
  let parts: {
    filename: string
    mimetype?: string
    folder: string
    key: string
  }[] = []
  const formFileEntries = files.getAll("files")
  let filesObjects: Record<string, File> = {}
  for (let i = 0; i < formFileEntries.length; i++) {
    const file = formFileEntries[i] as File
    if (file.size > MAX_FILE_SIZE) {
      return {
        files: [],
        success: false,
        errorType: FileUploadError.FILE_SIZE_LIMIT_REACHED,
      }
    }
    const key = v4()
    parts.push({
      filename: file.name,
      mimetype: file.type,
      folder: uid,
      key,
    })
    filesObjects[key] = file
  }

  const signedParts = await uploadSignedFiles(parts)

  const successfulUploads: IFile[] = []

  let hasAllSucceeded = true
  for (const part of signedParts) {
    const file = filesObjects[part.key]
    if (!file) {
      continue
    }

    try {
      const myHeaders = new Headers()
      myHeaders.append("Content-Type", file.type)
      // myHeaders.append("Content-Length", file.size.toString())

      const res = await fetch(part.url, {
        method: "PUT",
        body: file,
        headers: myHeaders,
      })
      if (res.status === 200) {
        const { code } = await confirmFileUpload(part.uid)
        if (code === StatusCode.Ok) {
          successfulUploads.push({ ...part, uploaded: true })
        } else {
          hasAllSucceeded = false
        }
      } else {
        hasAllSucceeded = false
      }
    } catch (error) {
      hasAllSucceeded = false
      console.error(error)
    }
  }

  return {
    files: successfulUploads,
    success: hasAllSucceeded,
    errorType: FileUploadError.OTHER,
  }
}

// export const uploadSignedFiles = async (
//   parts: { filename: string; mimetype?: string; folder: string; key: string }[],
// ): Promise<IFile[]> => {
//   return await fetcher(`/storage/signed`, {
//     method: "POST",
//     body: JSON.stringify({ parts }),
//   })
// }
export const uploadSignedFiles = async (
  parts: { filename: string; mimetype?: string; folder: string; key: string }[],
): Promise<IFile[]> => {
  // Must carry the tab's active space: the file node is created under the
  // resolved space's root folder with CREATED_BY its space_user. Without
  // this header the file lands in the token's default space, and the
  // subsequent upload_complete (sent via FetchClient WITH the header)
  // can't match it — uploads then fail for multi-space users (7N-19).
  const response = await fetch(
    `${API_URL}/storage/signed`,
    withTabSpaceHeader({
      method: "POST",
      credentials: COOKIE_AUTH_ENABLED ? "include" : "same-origin",
      headers: {
        "Content-Type": "application/json",
        ...(COOKIE_AUTH_ENABLED
          ? getCsrfHeaders()
          : { Authorization: `Bearer ${getToken()}` }),
      },
      body: JSON.stringify({ parts }),
    }),
  )

  if (!response.ok) {
    throw new Error(
      `/storage/signed failed: ${response.status} ${await response.text()}`,
    )
  }

  return response.json()
}

export const moveFiles = async (
  files: string[],
  destination: string,
): Promise<{ success: boolean }> => {
  return await fetcher("/storage/files/move", {
    method: "PUT",
    body: JSON.stringify({ files, destination }),
  })
}

export const moveFolders = async (
  folders: string[],
  destination: string,
): Promise<{ success: boolean }> => {
  return await fetcher("/storage/folders/move", {
    method: "PUT",
    body: JSON.stringify({ folders, destination }),
  })
}

export const updateFoldername = async (
  uid: string,
  name: string,
): Promise<{ success: boolean }> => {
  return await fetcher(`/storage/folders/${uid}`, {
    method: "PUT",
    body: JSON.stringify({ name }),
  })
}

export const updateFilename = async (
  uid: string,
  filename: string,
): Promise<IFile> => {
  return await fetcher(`/storage/files/${uid}`, {
    method: "PUT",
    body: JSON.stringify({ filename }),
  })
}

export const removeAttachments = async (attachments: string[]) => {
  const res: { data: string[] } = await fetcher(`/storage/attachments`, {
    method: "DELETE",
    body: JSON.stringify({ attachments }),
  })
  return res?.data ?? []
}

export const deleteFiles = async (files: string[]) => {
  if (files.length === 0) {
    return []
  }
  const res: { data: string[] } = await fetcher(`/storage/files`, {
    method: "DELETE",
    body: JSON.stringify({ files }),
  })
  return res?.data ?? []
}

export const deleteFolders = async (folders: string[]) => {
  if (folders.length === 0) {
    return
  }
  return await fetcher(`/storage/folders`, {
    method: "DELETE",
    body: JSON.stringify({ folders }),
  })
}
